import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Store,
  Users,
  CreditCard,
  TrendingUp,
  FileText,
  X
} from 'lucide-react'
import Logo from '../Logo'

interface AdminSidebarProps {
  isOpen: boolean
  onClose: () => void
  onToggle: () => void
}

interface NavItem {
  name: string
  path: string
  icon: React.ComponentType<{ className?: string }>
}

const navigation: NavItem[] = [
  { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
  { name: 'Restaurants', path: '/admin/restaurants', icon: Store },
  { name: 'Users', path: '/admin/users', icon: Users },
  { name: 'Subscriptions', path: '/admin/subscriptions', icon: CreditCard },
  { name: 'Revenue', path: '/admin/revenue', icon: TrendingUp },
  { name: 'Documentazione', path: '/admin/documentation', icon: FileText },
]

export function AdminSidebar({ isOpen, onClose, onToggle }: AdminSidebarProps) {
  const location = useLocation()

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700
          transition-all duration-300 ease-in-out
          ${isOpen ? 'w-64' : 'w-0 md:w-20'}
          overflow-hidden
        `}
      >
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700">
          {isOpen && (
            <>
              <div className="flex items-center gap-2">
                <Logo size={32} />
                <span className="font-bold text-lg text-gray-900 dark:text-white">
                  Super Admin
                </span>
              </div>
              <button
                onClick={onClose}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
              >
                <X className="w-5 h-5" />
              </button>
            </>
          )}
          {!isOpen && (
            <button
              onClick={onToggle}
              className="hidden md:block p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 mx-auto"
            >
              <LayoutDashboard className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {navigation.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/')

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                  ${isActive
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }
                  ${!isOpen && 'md:justify-center'}
                `}
                onClick={() => {
                  // Close sidebar on mobile after navigation
                  if (window.innerWidth < 768) {
                    onClose()
                  }
                }}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {isOpen && (
                  <span className="font-medium">{item.name}</span>
                )}
              </Link>
            )
          })}
        </nav>

        {/* Footer - Collapse/Expand Button (Desktop Only) */}
        {isOpen && (
          <div className="absolute bottom-4 left-0 right-0 px-4 hidden md:block">
            <button
              onClick={onToggle}
              className="w-full px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
            >
              Collapse Sidebar
            </button>
          </div>
        )}
      </aside>
    </>
  )
}
